import { Component, h } from 'preact'
import axios from 'axios'
import tracker from '../../components/tracker/publish'
import SSOOverviewPresenter from './SSOOverviewPresenter'
import SAMLSetup from './SAMLSetup'
import OpenIDSetup from "./OpenIDSetup";
import AlertComponent from "../ui-components/AlertComponent"
import TokenSetup from "./TokenSetup";
import {Configuration, Field, FieldList, Provider, ProviderList} from "./types";

interface Props {}

interface State {
  ssoProxyUrl: string
  providers: ProviderList
  alert: {
    visible: boolean,
    message?: string,
    type?: string | 'success' | 'error'
  },
  isLoading: boolean
}

const CONFIGURATION_URL = '/integration/sso/providers'
const BASE_PROVIDER_CONFIGURATION_URL = '/integration/sso/configuration'
const BASE_INSTALL_URL = '/integration/sso/install'

const PROVIDER_MAP = {
  saml_v2: 'saml',
  token_v2: 'token',
  openidconnect: 'openidconnect'
}

class SSOOverview extends Component<Props, State> {
  constructor () {
    super()
    this.state = {
      ssoProxyUrl: '',
      providers: [
        {
          name: 'saml',
          label: 'SAML 2',
          ...this.getDefaultProviderProperties(),
          fields: [
            {
              name: 'idp_url',
              label: 'Single Sign-On URL*',
              help: 'Identity Provider Authentication URL',
              required: true
            },
            {
              name: 'idp_public_key',
              label: 'Public Key*',
              help: 'Identity Provider Public Key in X.509 format',
              required: true
            }
          ]
        },
        {
          name: 'openidconnect',
          label: 'Open ID',
          ...this.getDefaultProviderProperties(),
          fields: [
            {
              name: 'auth_uri',
              label: 'Auth URI*',
              required: true
            },
            {
              name: 'client_id',
              label: 'Client ID*',
              required: true
            },
            {
              name: 'client_secret',
              label: 'Client Secret*',
              required: true
            },
            {
              name: 'issuer',
              label: 'Issuer*',
              required: true
            },
            {
              name: 'scope',
              label: 'Scope*',
              required: true
            },
            {
              name: 'token_uri',
              label: 'Token URI*',
              required: true
            }
          ],
        },
        {
          name: 'token',
          label: 'Token',
          ...this.getDefaultProviderProperties(),
          fields: [
            {
              name: 'login_url',
              label: 'Login URL*',
              required: true
            },
            {
              name: 'public_key',
              label: 'Public Key*',
              required: true
            }
          ]
        }
      ],
      alert: {
        visible: false,
        message: '',
        type: ''
      },
      isLoading: true
    }

    this.onInstall = this.onInstall.bind(this)
  }

  getDefaultProviderProperties () {
    return {
      isSetupVisible: false,
      isInstalled: false,
      onInstall: this.onInstall.bind(this),
      onEdit: this.onEdit.bind(this),
      configuration: {},
      errors: {}
    }
  }

  async componentDidMount(): Promise<void> {
    try {
      const response = await axios.get(CONFIGURATION_URL)
      if(response.data) {
        const { ssoProxyUrl, selfServicedSSOProvidersSettings } = response.data
        this.setState({ ssoProxyUrl })
        Object.keys(selfServicedSSOProvidersSettings).forEach(providerName => {
          const isInstalled = Boolean(selfServicedSSOProvidersSettings[providerName])
          // @ts-ignore
          this.updateProvider({ name: PROVIDER_MAP[providerName] }, { isInstalled })
        })
      }
      this.setState({ isLoading: false })
    } catch (e) {
      this.setState({ isLoading: false })
      throw e
    }
  }

  async onInstall (provider: Provider) {
    try {

      await axios.post(BASE_INSTALL_URL, {
        provider: provider.name
      })

      this.toggleProviderSetup(provider)
    } catch (e) {
      this.setState({
        alert: {
          visible: true,
          message: `Something went wrong`,
          type: 'error'
        }
      })
      throw e
    }
  }

  onEdit (provider: Provider) {
    this.toggleProviderSetup(provider)
  }

  toggleProviderSetup(provider: Provider) {
    this.updateProvider(provider, { isSetupVisible: !provider.isSetupVisible })
  }

  updateProvider(provider: { name: any }, property: {}) {
    const { providers } = this.state

    const index = providers.findIndex(obj => obj.name === provider.name);

    if(index < 0) return

    const updatedProviders = { ...providers[index], ...property };

    this.setState( { providers: [
        ...providers.slice(0, index),
        updatedProviders,
        ...providers.slice(index + 1),
      ]} )
  }

  onInstallationSucceeded (provider: Provider) {
    this.toggleProviderSetup(provider)
    const propsToUpdate = [
      { isInstalled: true },
      { errors: {} }
    ]

    propsToUpdate.forEach(prop => this.updateProvider(provider, prop))
    this.setState({
      alert: {
        visible: true,
        message: `${provider.label} is successfully installed`,
        type: 'success'
      }
    })

    const { userid, name, role } = window.inSidedData.user
    tracker.trackEvent('userevent', {
      type: 'SSO Configuration Saved',
      sso_type: provider.name,
      user_id: userid,
      user_name: name,
      user_primary_role: role
    })
  }

  async onProviderConfigurationSubmit (provider: Provider, configuration: Configuration) {
    try {
      const configurationUrl = `${BASE_PROVIDER_CONFIGURATION_URL}?provider=${provider.name}`

      await axios.post(configurationUrl, { ...configuration })

      this.onInstallationSucceeded(provider)
    } catch (e) {
      const { status, data } = e.response

      if(status === 422) {
        this.updateProvider(provider, { errors: data })
        return
      }

      this.setState({
        alert: {
          visible: true,
          type: 'error'
        }
      })
      throw e
    }
  }

  async onProviderMount (provider: Provider) {
    try {
      const configurationUrl = `${BASE_PROVIDER_CONFIGURATION_URL}?provider=${provider.name}`
      const response = await axios.get(configurationUrl)

      if(response.data) {
        const rawConfiguration = response.data
        const configuration = this.getEditableFields(rawConfiguration, provider.fields)

        this.updateProvider(provider, { configuration })
      }
    } catch (e) {
      throw e
    }
  }

  getEditableFields(configuration: Configuration, fields: FieldList) {
    const editableConfigurationFields = fields.map((field: Field) => field.name)
    return Object.keys(configuration)
        .filter(key => editableConfigurationFields.includes(key))
        .reduce((filtered: { [index: string] : any }, key: string) => {
          filtered[key] = configuration[key]
          return filtered
        }, {})
  }

  renderProviderSetup () {
    const { ssoProxyUrl, providers } = this.state

    const provider = providers.filter(provider => provider.isSetupVisible)[0]

    if (provider) {

      const commonProps = {
        provider,
        onMount: () => this.onProviderMount(provider),
        onSubmit: (configuration: Configuration) => this.onProviderConfigurationSubmit(provider, configuration)
      }

      if (provider.name === 'saml') {
        return <SAMLSetup ssoProxyUrl={ssoProxyUrl} {...commonProps} />
      }

      if (provider.name === 'openidconnect') {
        return <OpenIDSetup {...commonProps} />
      }

      if (provider.name === 'token') {
        return <TokenSetup {...commonProps} />
      }
    }

    return <SSOOverviewPresenter providers={providers} />
  }

  render (_: Props, {  alert, isLoading }: State) {
    if(isLoading) return null
    return (
      <div>
        { alert.visible && <AlertComponent
          messageType={alert.type}
          message={alert.message}
          isVisibleFor={5000}
          sticky
          onTimeoutEnd={() => this.setState({ alert: { visible: false } })}
          onClose={() => this.setState({ alert: { visible: false } })}
        /> }
        { this.renderProviderSetup() }
      </div>
    )
  }
}

export default SSOOverview
